import { useCallback, useState } from 'react'
import { getNoteText, removeNoteState, updateNoteState } from './pendingApplicationsUtil'

/**
 * State returned by useNoteModal hook
 */
export interface UseNoteModalReturn {
  showNoteModal: boolean
  noteText: string
  selectedAppId: string | null
  savedNotes: Record<string, string>
  setNoteText: (text: string) => void
  openNote: (appId: string) => void
  saveNote: () => void
  closeNote: () => void
  clearNote: (appId: string) => void
  clearNoteText: () => void
}

/**
 * Custom hook for managing note modal state and operations
 * Handles:
 * - Modal visibility
 * - Note text input
 * - Selected application ID
 * - Saved notes storage
 * 
 * @param initialSavedNotes - Initial saved notes state
 * @returns Note modal state and handler functions
 */
export const useNoteModal = (
  initialSavedNotes: Record<string, string> = {}
): UseNoteModalReturn => {
  const [showNoteModal, setShowNoteModal] = useState(false)
  const [noteText, setNoteText] = useState('')
  const [selectedAppId, setSelectedAppId] = useState<string | null>(null)
  const [savedNotes, setSavedNotes] = useState<Record<string, string>>(initialSavedNotes)

  /**
   * Open note modal and load existing note if any
   * @param appId - Application ID to open note for
   */
  const openNote = useCallback(
    (appId: string) => {
      const appIdStr = String(appId)
      setSelectedAppId(appIdStr)
      setNoteText(getNoteText(savedNotes, appIdStr))
      setShowNoteModal(true)
    },
    [savedNotes]
  )

  /**
   * Save note and close modal
   */
  const saveNote = useCallback(() => {
    if (!selectedAppId) {
      closeNote()
      return
    }

    setSavedNotes((prev) => updateNoteState(prev, selectedAppId, noteText))
    closeNote()
  }, [selectedAppId, noteText])

  /**
   * Close modal and reset state
   */
  const closeNote = useCallback(() => {
    setShowNoteModal(false)
    setSelectedAppId(null)
    setNoteText('')
  }, [])

  /**
   * Clear note for specific application
   * @param appId - Application ID to clear note for
   */
  const clearNote = useCallback((appId: string) => {
    setSavedNotes((prev) => removeNoteState(prev, String(appId)))
  }, [])

  /**
   * Clear note text in modal (used by clear button)
   */
  const clearNoteText = useCallback(() => {
    setNoteText('')
  }, [])

  return {
    showNoteModal,
    noteText,
    selectedAppId,
    savedNotes,
    setNoteText,
    openNote,
    saveNote,
    closeNote,
    clearNote,
    clearNoteText,
  }
}

/**
 * Custom hook for managing application loading state
 * Tracks which application is currently being processed
 */
export interface UseApplicationLoadingReturn {
  actionLoadingId: string | null
  setActionLoadingId: (id: string | null) => void
  isLoading: (appId: string) => boolean
}

/**
 * Hook to manage loading state during application actions
 * @returns Loading state and utility functions
 */
export const useApplicationLoading = (): UseApplicationLoadingReturn => {
  const [actionLoadingId, setActionLoadingId] = useState<string | null>(null)

  /**
   * Check if specific application is loading
   * @param appId - Application ID to check
   * @returns True if application is currently loading
   */
  const isLoading = useCallback(
    (appId: string): boolean => {
      return actionLoadingId === String(appId)
    },
    [actionLoadingId]
  )

  return {
    actionLoadingId,
    setActionLoadingId,
    isLoading,
  }
}